/**
 * Convert a roadmap detail into the content snapshot that undo/redo and the
 * version history restore. Only content fields are kept: the roadmap's name
 * and notes, ids (so a restore puts the very same items back), dates, names,
 * colors, templates. Order is meaningful (positions are rebuilt from it);
 * view state (collapsed rows, timestamps, sharing) is not included.
 */
import type { PhaseTemplate, RoadmapSnapshot, SnapshotItem } from "./types";

interface ItemLike {
  id: string;
  title: string;
  description: string | null;
  startDate: string;
  endDate: string;
  color: string;
  phases: { id: string; name: string; startDate: string; endDate: string; color: string }[];
}

export function toSnapshot(detail: {
  roadmap?: { name: string; description: string | null };
  items: ItemLike[];
  phaseTemplates: Pick<PhaseTemplate, "id" | "name" | "color" | "startPct" | "endPct">[];
}): RoadmapSnapshot {
  return {
    ...(detail.roadmap
      ? { roadmap: { name: detail.roadmap.name, description: detail.roadmap.description } }
      : {}),
    items: detail.items.map(
      (i): SnapshotItem => ({
        id: i.id,
        title: i.title,
        description: i.description ?? null,
        startDate: i.startDate,
        endDate: i.endDate,
        color: i.color,
        phases: i.phases.map((p) => ({
          id: p.id,
          name: p.name,
          startDate: p.startDate,
          endDate: p.endDate,
          color: p.color,
        })),
      }),
    ),
    phaseTemplates: detail.phaseTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      color: t.color,
      startPct: t.startPct,
      endPct: t.endPct,
    })),
  };
}

/** Stable text form, used to tell "nothing changed" apart from a real change. */
export function snapshotKey(s: RoadmapSnapshot): string {
  return JSON.stringify(s);
}

/**
 * Coarse comparison for the version list: how many items were added, removed
 * or changed between two snapshots (phases count as part of their item), and
 * whether the roadmap's name or notes differ.
 */
export function diffSnapshots(
  from: RoadmapSnapshot,
  to: RoadmapSnapshot,
): { added: number; removed: number; changed: number; roadmap: boolean } {
  const fromById = new Map(from.items.map((i) => [i.id, JSON.stringify(i)]));
  const toById = new Map(to.items.map((i) => [i.id, JSON.stringify(i)]));
  let added = 0;
  let removed = 0;
  let changed = 0;
  for (const [id, json] of toById) {
    const prev = fromById.get(id);
    if (prev === undefined) added += 1;
    else if (prev !== json) changed += 1;
  }
  for (const id of fromById.keys()) if (!toById.has(id)) removed += 1;
  const roadmap =
    !!from.roadmap &&
    !!to.roadmap &&
    (from.roadmap.name !== to.roadmap.name ||
      (from.roadmap.description ?? null) !== (to.roadmap.description ?? null));
  return { added, removed, changed, roadmap };
}
